import {
  SystemSetting,
  SystemSettingConstraints,
  SystemSettingValue,
  SystemSettingValueType,
} from "../../../types/system-setting.type";
import { isDurationMinutesSetting, settingOptionLabel } from "./settingMeta";

/**
 * The edit dialog's working copy. A single value is typed as `text`; a list is edited as
 * `items`, one chip per item. Both stay strings until the draft is parsed for saving.
 */
export interface SettingDraft {
  text: string;
  items: string[];
}

type SettingDraftResult =
  | { value: SystemSettingValue; error: null }
  | { value: null; error: string };

const WHOLE_NUMBER_SCALE = 0;
const NUMBER_PATTERN = /^-?\d+(\.\d+)?$/;
const REQUIRED_ERROR = "กรุณากรอกค่า";
const NOT_A_NUMBER_ERROR = "ต้องเป็นตัวเลข";
const WHOLE_NUMBER_ERROR = "ต้องเป็นจำนวนเต็ม";
const CHOICE_REQUIRED_ERROR = "กรุณาเลือกค่า";
const MINUTES_PER_HOUR = 60;
const DIGITS_PATTERN = /^\d*$/;

/** Shown for a list setting that currently holds no items. */
export const EMPTY_LIST_TEXT = "ไม่มี";

const isListType = (valueType: SystemSettingValueType): boolean =>
  valueType === "integer_list" || valueType === "text_list";

const isWholeNumberType = (valueType: SystemSettingValueType): boolean =>
  valueType === "integer" || valueType === "integer_list";

const isNumberType = (valueType: SystemSettingValueType): boolean =>
  valueType === "decimal" || valueType === "integer";

export const toSettingDraft = (value: SystemSettingValue): SettingDraft =>
  Array.isArray(value)
    ? { text: "", items: value.map(String) }
    : { text: String(value), items: [] };

/** Minutes as Thai hours + minutes: 150 → "2 ชม. 30 นาที", 60 → "1 ชม.", 5 → "5 นาที". */
export const formatDurationMinutes = (totalMinutes: number): string => {
  const hours = Math.floor(totalMinutes / MINUTES_PER_HOUR);
  const minutes = totalMinutes % MINUTES_PER_HOUR;
  if (hours === 0) return `${minutes} นาที`;
  return minutes === 0 ? `${hours} ชม.` : `${hours} ชม. ${minutes} นาที`;
};

/** A minutes draft split for the hours + minutes inputs; blank stays blank. */
export const splitDurationDraft = (text: string): { hours: string; minutes: string } => {
  const total = Number(text);
  if (!text.trim() || !Number.isInteger(total) || total < 0) return { hours: "", minutes: text };
  return {
    hours: String(Math.floor(total / MINUTES_PER_HOUR)),
    minutes: String(total % MINUTES_PER_HOUR),
  };
};

/**
 * The hours + minutes inputs joined back into the minutes draft. A blank side counts as 0;
 * anything but digits is passed through as-is so parsing reports it as not a number.
 */
export const joinDurationDraft = (hours: string, minutes: string): string => {
  const hoursText = hours.trim();
  const minutesText = minutes.trim();
  if (!hoursText && !minutesText) return "";
  if (!DIGITS_PATTERN.test(hoursText) || !DIGITS_PATTERN.test(minutesText))
    return `${hoursText}:${minutesText}`;
  return String(Number(hoursText || 0) * MINUTES_PER_HOUR + Number(minutesText || 0));
};

/** A value as the page shows it; `key` lets a choice show its Thai name and minutes show as hours. */
export const formatSettingValue = (value: SystemSettingValue, key?: string): string => {
  if (typeof value === "number" && isDurationMinutesSetting(key))
    return formatDurationMinutes(value);
  if (typeof value === "string" && key) return settingOptionLabel(key, value);
  if (!Array.isArray(value)) return String(value);
  return value.length > 0 ? value.join(", ") : EMPTY_LIST_TEXT;
};

export const isSameSettingValue = (
  first: SystemSettingValue,
  second: SystemSettingValue,
): boolean => JSON.stringify(first) === JSON.stringify(second);

/** Chip input: trimmed, blanks dropped, and a repeated item kept once. */
export const normalizeListItems = (items: string[]): string[] =>
  Array.from(new Set(items.map((item) => item.trim()).filter(Boolean)));

/** Where the value differs from the code default, the default it would go back to. */
export const defaultValueHint = (
  setting: Pick<SystemSetting, "value" | "default_value"> & { key?: string },
): string | null =>
  isSameSettingValue(setting.value, setting.default_value)
    ? null
    : `ค่าเริ่มต้น ${formatSettingValue(setting.default_value, setting.key)}`;

const effectiveScale = (
  valueType: SystemSettingValueType,
  constraints: SystemSettingConstraints,
): number | undefined =>
  isWholeNumberType(valueType) ? WHOLE_NUMBER_SCALE : constraints.scale;

const rangeText = ({ min, max }: SystemSettingConstraints): string | null => {
  if (min !== undefined && max !== undefined) return `${min}–${max}`;
  if (min !== undefined) return `ไม่น้อยกว่า ${min}`;
  if (max !== undefined) return `ไม่เกิน ${max}`;
  return null;
};

const lengthText = ({ min_length, max_length }: SystemSettingConstraints): string | null => {
  if (min_length !== undefined && min_length === max_length)
    return `ยาว ${min_length} ตัวอักษร`;
  if (min_length !== undefined && max_length !== undefined)
    return `ยาว ${min_length}–${max_length} ตัวอักษร`;
  if (min_length !== undefined) return `ยาวอย่างน้อย ${min_length} ตัวอักษร`;
  if (max_length !== undefined) return `ยาวไม่เกิน ${max_length} ตัวอักษร`;
  return null;
};

/** An empty list is allowed when `min_items` is 0, so only a positive minimum is worth saying. */
const itemCountText = ({ min_items, max_items }: SystemSettingConstraints): string | null => {
  const minimum = min_items !== undefined && min_items > 0 ? min_items : undefined;
  if (minimum !== undefined && max_items !== undefined) return `${minimum}–${max_items} รายการ`;
  if (minimum !== undefined) return `อย่างน้อย ${minimum} รายการ`;
  if (max_items !== undefined) return `ไม่เกิน ${max_items} รายการ`;
  return null;
};

const scaleText = (scale: number | undefined): string | null => {
  if (scale === undefined) return null;
  return scale === WHOLE_NUMBER_SCALE ? "จำนวนเต็ม" : `ทศนิยมไม่เกิน ${scale} ตำแหน่ง`;
};

const durationRangeText = ({ min, max }: SystemSettingConstraints): string | null => {
  if (min === undefined || max === undefined) return null;
  return `${formatDurationMinutes(min)} – ${formatDurationMinutes(max)}`;
};

/** The rules a value is checked against, as one line under the input. */
export const describeConstraints = (
  valueType: SystemSettingValueType,
  constraints: SystemSettingConstraints,
  key?: string,
): string => {
  if (valueType === "choice") return "";
  if (isDurationMinutesSetting(key)) return durationRangeText(constraints) ?? "";
  const hasNumbers = isNumberType(valueType) || valueType === "integer_list";
  const range = hasNumbers ? rangeText(constraints) : null;
  const parts = [
    isListType(valueType) ? itemCountText(constraints) : null,
    hasNumbers ? scaleText(effectiveScale(valueType, constraints)) : null,
    range && (isListType(valueType) ? `แต่ละค่า ${range}` : range),
    hasNumbers ? null : lengthText(constraints),
  ];
  return parts.filter(Boolean).join(" · ");
};

const decimalPlaces = (text: string): number => text.split(".")[1]?.length ?? 0;

const rangeError = (number: number, { min, max }: SystemSettingConstraints): string | null => {
  const isBelow = min !== undefined && number < min;
  const isAbove = max !== undefined && number > max;
  if (!isBelow && !isAbove) return null;
  if (min !== undefined && max !== undefined) return `ต้องอยู่ระหว่าง ${min}–${max}`;
  return isBelow ? `ต้องไม่น้อยกว่า ${min}` : `ต้องไม่เกิน ${max}`;
};

const numberError = (
  text: string,
  constraints: SystemSettingConstraints,
  scale: number | undefined,
): string | null => {
  if (!NUMBER_PATTERN.test(text)) return NOT_A_NUMBER_ERROR;
  if (scale !== undefined && decimalPlaces(text) > scale)
    return scale === WHOLE_NUMBER_SCALE
      ? WHOLE_NUMBER_ERROR
      : `ทศนิยมได้ไม่เกิน ${scale} ตำแหน่ง`;
  return rangeError(Number(text), constraints);
};

const lengthError = (
  text: string,
  { min_length, max_length }: SystemSettingConstraints,
): string | null => {
  const isTooShort = min_length !== undefined && text.length < min_length;
  const isTooLong = max_length !== undefined && text.length > max_length;
  return isTooShort || isTooLong ? `ต้อง${lengthText({ min_length, max_length })}` : null;
};

const itemCountError = (
  count: number,
  { min_items, max_items }: SystemSettingConstraints,
): string | null => {
  if (min_items !== undefined && count < min_items)
    return `ต้องมีอย่างน้อย ${min_items} รายการ`;
  if (max_items !== undefined && count > max_items)
    return `มีได้ไม่เกิน ${max_items} รายการ`;
  return null;
};

const failure = (error: string): SettingDraftResult => ({ value: null, error });

const success = (value: SystemSettingValue): SettingDraftResult => ({ value, error: null });

const parseListDraft = (
  valueType: SystemSettingValueType,
  constraints: SystemSettingConstraints,
  items: string[],
): SettingDraftResult => {
  const countError = itemCountError(items.length, constraints);
  if (countError) return failure(countError);
  const isIntegerList = valueType === "integer_list";
  for (const item of items) {
    const error = isIntegerList
      ? numberError(item, constraints, WHOLE_NUMBER_SCALE)
      : lengthError(item, constraints);
    if (error) return failure(`"${item}" ${error}`);
  }
  return success(isIntegerList ? items.map(Number) : items);
};

/**
 * The draft as the value PATCH /system-settings/:key takes, or the first rule it breaks.
 *
 * Mirrors only the bounds the server sends in `constraints`. Rules between keys or against
 * master data (a code that must exist, a list that must ascend) stay the server's and come
 * back as its message.
 */
export const parseSettingDraft = (
  valueType: SystemSettingValueType,
  constraints: SystemSettingConstraints,
  draft: SettingDraft,
  key?: string,
): SettingDraftResult => {
  if (isListType(valueType)) return parseListDraft(valueType, constraints, draft.items);

  const text = draft.text.trim();
  if (valueType === "choice")
    return constraints.options?.includes(text) ? success(text) : failure(CHOICE_REQUIRED_ERROR);
  if (!text) return failure(REQUIRED_ERROR);

  if (isNumberType(valueType)) {
    const error = numberError(text, constraints, effectiveScale(valueType, constraints));
    // A duration's bounds read as hours + minutes, like its inputs.
    const range = isDurationMinutesSetting(key) ? durationRangeText(constraints) : null;
    if (error && range && NUMBER_PATTERN.test(text) && !text.includes("."))
      return failure(`ต้องอยู่ระหว่าง ${range}`);
    return error ? failure(error) : success(Number(text));
  }

  const error = lengthError(text, constraints);
  return error ? failure(error) : success(text);
};
